// Per-dog class enrollments. Puppy K, Obedience, Rally, Nose Work, etc.
// Each entry has a type, dates, status, and optional trainer/facility.
import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO, subDays } from "date-fns";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import { Trash2, Plus, GraduationCap, X, BellRing, Pencil } from "lucide-react";

const TYPES = [
  "Puppy Class", "Obedience", "Agility", "Rally", "Nose Work",
  "Trick", "Conformation", "Sport Foundations", "Rehab", "Other",
];

const STATUSES = [
  { value: "active",    label: "Active",    cls: "bg-primary/10 text-primary" },
  { value: "planned",   label: "Planned",   cls: "bg-accent/40 text-accent-foreground" },
  { value: "completed", label: "Completed", cls: "bg-green-100 text-green-800" },
  { value: "dropped",   label: "Dropped",   cls: "bg-secondary/60 text-secondary-foreground" },
];
const STATUS_META = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

const blank = () => ({
  name: "", class_type: TYPES[0], trainer_or_facility: "",
  start_date: "", end_date: "", status: "active", notes: "",
});

export default function ClassesSection({ dogId }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);   // class row being inline-edited
  const [editForm, setEditForm] = useState(blank());
  const [editingOriginalStart, setEditingOriginalStart] = useState("");

  const load = useCallback(async () => {
    if (!dogId) return;
    try {
      const { data } = await api.get(`/dogs/${dogId}/classes`);
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [dogId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post(`/dogs/${dogId}/classes`, form);
      toast.success("Class added");
      setForm(blank());
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const cycleStatus = async (cls) => {
    const order = ["active", "planned", "completed", "dropped"];
    const next = order[(order.indexOf(cls.status) + 1) % order.length];
    setItems((prev) => prev.map((c) => (c.id === cls.id ? { ...c, status: next } : c)));
    try {
      await api.put(`/dogs/${dogId}/classes/${cls.id}`, { status: next });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update");
      await load();
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/dogs/${dogId}/classes/${id}`);
      await load();
    } catch { toast.error("Could not delete"); }
  };

  // One-tap reminder — schedules a one-time email nudge for 8am the day
  // before the class's start_date. If the start_date has already passed or
  // is missing, nothing to remind about (button is hidden in that case).
  const remindMe = async (cls) => {
    if (!cls.start_date) return;
    const now = new Date();
    let target;
    try {
      target = subDays(parseISO(cls.start_date), 1);
    } catch {
      toast.error("Class start date is invalid");
      return;
    }
    if (target <= now) {
      toast.error("Class starts too soon to schedule a day-before reminder");
      return;
    }
    try {
      const { data: rem } = await api.post(`/reminders`, {
        dog_id: dogId,
        kind: "custom",
        title: `Tomorrow: ${cls.name}`,
        notes: [cls.trainer_or_facility ? `With ${cls.trainer_or_facility}` : "", cls.notes || ""].filter(Boolean).join("\n\n"),
        frequency: "once",
        days_of_week: [],
        time_of_day: "08:00",
        tz_offset_minutes: new Date().getTimezoneOffset(),
        run_once_date: format(target, "yyyy-MM-dd"),
        enabled: true,
      });
      // Link reminder to the class so we can rebuild it when start_date moves.
      if (rem?.id) {
        try {
          await api.put(`/dogs/${dogId}/classes/${cls.id}`, { reminder_id: rem.id });
          setItems((prev) => prev.map((c) => (c.id === cls.id ? { ...c, reminder_id: rem.id } : c)));
        } catch { /* best-effort — reminder still exists standalone */ }
      }
      toast.success(`Reminder set for ${format(target, "MMM d")} at 8:00 AM`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not schedule reminder");
    }
  };

  // Reschedule the linked reminder to a new start_date. Called from the
  // toast action after a start-date edit.
  const rescheduleReminder = async (cls, newStartDate) => {
    if (!cls.reminder_id || !newStartDate) return;
    let target;
    try {
      target = subDays(parseISO(newStartDate), 1);
    } catch {
      toast.error("New start date is invalid");
      return;
    }
    const now = new Date();
    if (target <= now) {
      toast.error("New start is too soon to keep the day-before reminder");
      return;
    }
    try {
      // The reminders API requires a full ReminderIn body on PUT, so pull
      // the current record, then overwrite run_once_date.
      const { data: cur } = await api.get(`/reminders`);
      const rem = (cur?.items || []).find((r) => r.id === cls.reminder_id);
      if (!rem) {
        toast.error("Linked reminder is gone. Tap the bell to make a new one.");
        // Clear the stale link.
        await api.put(`/dogs/${dogId}/classes/${cls.id}`, { reminder_id: "" });
        setItems((prev) => prev.map((c) => (c.id === cls.id ? { ...c, reminder_id: "" } : c)));
        return;
      }
      await api.put(`/reminders/${rem.id}`, {
        dog_id: rem.dog_id || dogId,
        kind: rem.kind || "custom",
        title: rem.title,
        notes: rem.notes || "",
        frequency: "once",
        days_of_week: [],
        time_of_day: rem.time_of_day || "08:00",
        tz_offset_minutes: rem.tz_offset_minutes ?? new Date().getTimezoneOffset(),
        run_once_date: format(target, "yyyy-MM-dd"),
        enabled: true,
      });
      toast.success(`Reminder moved to ${format(target, "MMM d")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not reschedule");
    }
  };

  const startEdit = (cls) => {
    setEditingId(cls.id);
    setEditingOriginalStart(cls.start_date || "");
    setEditForm({
      name: cls.name || "",
      class_type: cls.class_type || TYPES[0],
      trainer_or_facility: cls.trainer_or_facility || "",
      start_date: cls.start_date || "",
      end_date: cls.end_date || "",
      status: cls.status || "active",
      notes: cls.notes || "",
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditingOriginalStart(""); };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editingId || !editForm.name.trim()) return;
    const cls = items.find((c) => c.id === editingId);
    const startChanged = (editForm.start_date || "") !== (editingOriginalStart || "");
    setSaving(true);
    try {
      await api.put(`/dogs/${dogId}/classes/${editingId}`, editForm);
      toast.success("Class updated");
      await load();
      const updatedCls = { ...cls, ...editForm };
      // If start date moved AND a reminder is linked, offer a one-tap rebuild.
      if (startChanged && cls?.reminder_id && editForm.start_date) {
        toast(
          `Class start moved. Reschedule the day-before reminder?`,
          {
            duration: 12000,
            action: {
              label: "Reschedule",
              onClick: () => rescheduleReminder(updatedCls, editForm.start_date),
            },
          }
        );
      } else if (startChanged && cls?.reminder_id && !editForm.start_date) {
        toast(`Class start was cleared. The linked reminder is now stale — you may want to remove it.`);
      }
      cancelEdit();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const active = useMemo(() => items.filter((c) => c.status === "active"), [items]);

  return (
    <div className="space-y-4" data-testid="classes-section">
      <SectionTitle
        overline="Enrollments"
        title="Classes"
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setShowForm((s) => !s); setForm(blank()); }}
            data-testid="class-toggle-form"
          >
            {showForm ? <><X size={13} className="mr-1" /> Cancel</> : <><Plus size={13} className="mr-1" /> Add class</>}
          </Button>
        }
      />
      <p className="text-sm text-muted-foreground max-w-2xl -mt-2">
        Log every class you enrol in — Puppy K, Obedience, Rally, Nose Work.
        Click the status pill to cycle Active → Planned → Completed → Dropped.
      </p>

      {showForm ? (
        <Card className="border-border shadow-none rounded-lg" data-testid="class-form">
          <CardContent className="p-5 sm:p-6">
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Class name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder='e.g. "Puppy K Level 1", "Rally Novice"'
                  className="mt-1.5"
                  required
                  maxLength={100}
                  data-testid="class-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Type</Label>
                <select
                  value={form.class_type}
                  onChange={(e) => setForm({ ...form, class_type: e.target.value })}
                  className="mt-1.5 w-full border border-input rounded-md bg-card px-3 py-2 text-sm"
                  data-testid="class-type"
                >
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Status</Label>
                <select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="mt-1.5 w-full border border-input rounded-md bg-card px-3 py-2 text-sm"
                  data-testid="class-status"
                >
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Trainer / facility</Label>
                <Input
                  value={form.trainer_or_facility}
                  onChange={(e) => setForm({ ...form, trainer_or_facility: e.target.value })}
                  placeholder="Emma at K9 Central"
                  className="mt-1.5"
                  data-testid="class-trainer"
                />
              </div>
              <div className="grid grid-cols-2 gap-3 sm:col-span-1">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Start</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                    className="mt-1.5"
                    data-testid="class-start"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">End</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                    className="mt-1.5"
                    data-testid="class-end"
                  />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notes</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="What the class covers, homework, method, cost…"
                  className="mt-1.5 text-sm"
                  data-testid="class-notes"
                />
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <Button
                  type="submit"
                  disabled={saving || !form.name.trim()}
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="class-save"
                >
                  {saving ? "Saving…" : "Add class"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : null}

      {items.length === 0 && !showForm ? (
        <Card className="border-dashed border-border shadow-none rounded-lg">
          <CardContent className="p-6 text-sm text-muted-foreground text-center">
            No classes logged yet.
          </CardContent>
        </Card>
      ) : (
        <ul className="space-y-2" data-testid="classes-list">
          {items.map((c) => {
            const st = STATUS_META[c.status] || STATUS_META.active;
            return (
              <Card key={c.id} className="border-border shadow-none rounded-lg" data-testid={`class-item-${c.id}`}>
                <CardContent className="p-4 sm:p-5">
                  {editingId === c.id ? (
                    <form onSubmit={submitEdit} className="space-y-3" data-testid={`class-edit-form-${c.id}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label>
                          <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1.5" required data-testid={`class-edit-name-${c.id}`} />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Type</Label>
                          <select value={editForm.class_type} onChange={(e) => setEditForm({ ...editForm, class_type: e.target.value })} className="mt-1.5 w-full border border-border rounded-md bg-card px-3 py-2 text-sm">
                            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Trainer / facility</Label>
                          <Input value={editForm.trainer_or_facility} onChange={(e) => setEditForm({ ...editForm, trainer_or_facility: e.target.value })} className="mt-1.5" />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Start</Label>
                          <Input type="date" value={editForm.start_date} onChange={(e) => setEditForm({ ...editForm, start_date: e.target.value })} className="mt-1.5" data-testid={`class-edit-start-${c.id}`} />
                        </div>
                        <div>
                          <Label className="text-xs uppercase tracking-wide text-muted-foreground">End</Label>
                          <Input type="date" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} className="mt-1.5" />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Notes</Label>
                          <Textarea rows={2} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="mt-1.5 text-sm" />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={cancelEdit} data-testid={`class-edit-cancel-${c.id}`}>Cancel</Button>
                        <Button type="submit" size="sm" disabled={saving || !editForm.name.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid={`class-edit-save-${c.id}`}>
                          {saving ? "Saving…" : "Save class"}
                        </Button>
                      </div>
                    </form>
                  ) : (
                  <div className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                      <GraduationCap size={15} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-bold text-base leading-tight">{c.name}</p>
                        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground bg-secondary/60 rounded px-1.5 py-0.5">
                          {c.class_type}
                        </span>
                        <button
                          type="button"
                          onClick={() => cycleStatus(c)}
                          className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 hover:opacity-80 ${st.cls}`}
                          title="Click to cycle status"
                          data-testid={`class-status-pill-${c.id}`}
                        >
                          {st.label}
                        </button>
                        {c.reminder_id ? (
                          <span
                            className="text-[10px] uppercase tracking-wider rounded-full px-1.5 py-0.5 bg-primary/10 text-primary inline-flex items-center gap-1"
                            title="Day-before reminder is set"
                            data-testid={`class-reminder-linked-${c.id}`}
                          >
                            <BellRing size={9} /> reminder set
                          </span>
                        ) : null}
                      </div>
                      {c.trainer_or_facility ? (
                        <p className="mt-1 text-xs text-muted-foreground">with {c.trainer_or_facility}</p>
                      ) : null}
                      {(c.start_date || c.end_date) ? (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {c.start_date ? format(parseISO(c.start_date), "MMM d, yyyy") : "—"}
                          {c.end_date ? ` → ${format(parseISO(c.end_date), "MMM d, yyyy")}` : ""}
                        </p>
                      ) : null}
                      {c.notes ? (
                        <p className="mt-1.5 text-sm text-foreground/85 whitespace-pre-line">{c.notes}</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {c.start_date && (() => {
                        // Only show remind button while start_date is at least
                        // 1 day away — otherwise the day-before nudge can't be set.
                        try {
                          const start = parseISO(c.start_date);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return start > today;
                        } catch { return false; }
                      })() ? (
                        <button
                          type="button"
                          onClick={() => remindMe(c)}
                          className="text-muted-foreground hover:text-primary p-1"
                          title={c.reminder_id ? "Replace day-before reminder" : "Remind me the day before"}
                          data-testid={`class-remind-${c.id}`}
                        >
                          <BellRing size={13} />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => startEdit(c)}
                        className="text-muted-foreground hover:text-primary p-1"
                        title="Edit class"
                        data-testid={`class-edit-${c.id}`}
                      >
                        <Pencil size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(c.id)}
                        className="text-muted-foreground hover:text-destructive p-1"
                        title="Remove"
                        data-testid={`class-remove-${c.id}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </ul>
      )}

      {active.length > 0 ? (
        <p className="text-xs text-muted-foreground text-center" data-testid="classes-active-count">
          {active.length} active enrollment{active.length === 1 ? "" : "s"}
        </p>
      ) : null}
    </div>
  );
}
